import * as angular from "angular";

export interface SortState {
    column: string | null;
    direction: "asc" | "desc";
}

interface SortableHeaderCtrl {
    column: string;
    sort: SortState | undefined;
}

interface SortableHeaderScope extends angular.IScope {
    $ctrl: SortableHeaderCtrl;
}

const TEMPLATE_URL: string =
    "/angular/components/shared/sortable-header/sortable-header.directive.html";

/**
 * `vc-sortable-header` adds click-to-sort and aria-sort behaviour to a table
 * header cell. It is intentionally implemented with `scope: false` so it can
 * coexist with `md-column` (from `md.data.table`) on the same `<th>`. Both
 * `scope: true` and `scope: {}` would conflict with `md-column`'s isolated
 * scope and trigger `[$compile:multidir]` at runtime — AngularJS forbids two
 * directives on the same element from each requesting a new scope.
 *
 * Per-element state (`column`, `sort`) is published on a child scope created
 * manually in the link function and exposed as `$ctrl`, so the template's
 * `$ctrl.column` / `$ctrl.sort` references keep working without polluting
 * the parent scope. The column label is detached and re-inserted into the
 * template's `.vc-sortable-header-label` slot manually — AngularJS
 * transclusion is incompatible with `scope: false` on a multi-directive
 * element.
 */
const SortableHeaderDirective = (
    $compile: angular.ICompileService,
    $templateRequest: angular.ITemplateRequestService
): angular.IDirective => ({
    restrict: "A",
    scope: false,
    link: (
        scope: angular.IScope,
        element: angular.IAugmentedJQuery,
        attrs: angular.IAttributes
    ): void => {
        const childScope: SortableHeaderScope =
            scope.$new() as SortableHeaderScope;
        const ctrl: SortableHeaderCtrl = {
            // `column` is a literal expression like "'gname'" — resolved once.
            column: scope.$eval((attrs as any).column),
            sort: undefined,
        };
        childScope.$ctrl = ctrl;

        const originalContents: angular.IAugmentedJQuery = element
            .contents()
            .detach() as angular.IAugmentedJQuery;

        $templateRequest(TEMPLATE_URL).then((template: string): void => {
            const compiled: angular.IAugmentedJQuery = $compile(
                angular.element(
                    `<span>${template}</span>`
                ).contents() as angular.IAugmentedJQuery
            )(childScope);
            element.append(compiled);
            const labelSlot: Element | null = element[0].querySelector(
                ".vc-sortable-header-label"
            );
            if (labelSlot) {
                angular.element(labelSlot).append(originalContents);
            }
        });

        // Track the host's sort state.
        scope.$watch(
            (attrs as any).sort,
            (newSort: SortState | undefined): void => {
                ctrl.sort = newSort;
                if (newSort && newSort.column === ctrl.column) {
                    element.attr(
                        "aria-sort",
                        newSort.direction === "asc" ? "ascending" : "descending"
                    );
                } else {
                    element.removeAttr("aria-sort");
                }
            },
            true
        );

        element.css("cursor", "pointer");
        element.on("click", (): void => {
            scope.$apply((): void => {
                scope.$eval((attrs as any).onChange, {
                    column: ctrl.column,
                });
            });
        });

        scope.$on("$destroy", (): void => {
            childScope.$destroy();
        });
    },
});

(SortableHeaderDirective as unknown as { $inject: string[] }).$inject = [
    "$compile",
    "$templateRequest",
];

angular
    .module("ecanvasserDirectives")
    .directive("vcSortableHeader", SortableHeaderDirective);
